<template>
    <div class="contact-page">
        <!-- 1. 顶部主视觉 (Hero Section) -->
        <section class="contact-hero">
            <div class="banner">
                <!-- 背景图片 -->
                <img src="https://qiniu-au.wifigsmalarm.com/images/page-banner.png" class="background-img" alt="背景图">

                <!-- 文字内容容器 -->
                <div class="text-content">
                    <h1>{{ t('contact.hero.title') }}</h1>
                    <p>{{ t('contact.hero.desc') }}</p>
                </div>
            </div>
        </section>

        <!-- 3. 留言表单与办公地址 (Form & Locations) -->
        <section class="form-location-section bg-light">
            <div class="container split-layout">

                <!-- 左侧：在线留言表单 -->
                <div class="form-container">
                    <h2>{{ t('contact.form.title') }}</h2>
                    <p class="form-subtitle">{{ t('contact.form.subtitle') }}</p>

                    <form @submit.prevent="submitForm" class="contact-form">
                        <!-- 伪装蜜罐字段（机器人遍历表单常会自动填充常见 input 名称，对正常用户完全不可见且不抢焦点） -->
                        <div style="opacity: 0; position: absolute; top: -9999px; left: -9999px; height: 0; width: 0; pointer-events: none;"
                            aria-hidden="true">
                            <input type="text" name="company_website" v-model="honeypot" tabindex="-1"
                                autocomplete="off" />
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label>{{ $t('product.form.name') }} </label>
                                <input type="text" v-model="formData.name"
                                    :placeholder="$t('product.form.namePlaceholder')" />
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="email">{{ t('contact.form.email') }}</label>
                                <input type="email" id="email" v-model="formData.email" required
                                    :placeholder="t('contact.form.emailPlaceholder')" />
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="inquiryType">{{ t('contact.form.inquiryType') }}</label>
                                <div class="select-wrapper">
                                    <select id="inquiryType" v-model="formData.order_type" required>
                                        <option value="" disabled>{{ t('contact.form.selectTopic') }}</option>
                                        <option v-for="type in inquiryTypes" :key="type.value" :value="type.value">
                                            {{ type.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="message">{{ t('contact.form.message') }}</label>
                                <textarea id="message" v-model="formData.remark" rows="5" required
                                    :placeholder="t('contact.form.messagePlaceholder')"></textarea>
                            </div>
                        </div>

                        <!-- Cloudflare Turnstile 验证组件 -->
                        <div class="form-turnstile">
                            <TurnstileWidget ref="turnstileRef" :sitekey="TURNSTILE_SITE_KEY"
                                @verify="onTurnstileSuccess" @expire="turnstileToken = ''"
                                @error="turnstileToken = ''" />
                        </div>

                        <button type="submit" class="btn-submit" :disabled="isSubmitting">
                            {{ isSubmitting ? t('contact.form.sending') : t('contact.form.submitBtn') }}
                        </button>

                        <!-- 提交成功提示 -->
                        <transition name="fade">
                            <div v-if="submitSuccess" class="success-message">
                                ✅ {{ t('contact.form.successMsg') }}
                            </div>
                        </transition>
                    </form>
                </div>

                <!-- 右侧：全球办公地点 -->
                <div class="locations-container">
                    <h2>{{ t('contact.location.title') }}</h2>

                    <div class="office-card">
                        <h4>{{ t('contact.location.officeName') }}</h4>
                        <p>{{ t('contact.location.address') }}</p>
                        <p class="office-phone">📞 {{ t('contact.location.phone') }}</p>
                    </div>
                </div>

            </div>
        </section>
    </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { Order } from '@/api/product'
import TurnstileWidget from '@/components/TurnstileWidget.vue'

const { t } = useI18n()

// --- 安全防护：Turnstile 与 蜜罐 ---
const turnstileRef = ref(null)
const turnstileToken = ref('')
const honeypot = ref('')
const TURNSTILE_SITE_KEY = '0x4AAAAAAFVXUplqQ584Lc_l'

const onTurnstileSuccess = (token) => {
    turnstileToken.value = token
}

const resetSecurityState = () => {
    turnstileToken.value = ''
    honeypot.value = ''
    if (turnstileRef.value && typeof turnstileRef.value.reset === 'function') {
        turnstileRef.value.reset()
    }
}

// --- 表单数据状态 ---
const formData = reactive({
    name: '',
    email: '',
    whatsapp: '',
    order_type: 1,
    remark: ''
})

// 使用 computed 包装咨询类型下拉选项，并提供中英文映射
const inquiryTypes = computed(() => [
    { value: 1, label: t('contact.inquiry.general') },
    { value: 2, label: t('contact.inquiry.sales') },
    { value: 3, label: t('contact.inquiry.support') },
    { value: 4, label: t('contact.inquiry.hardware') },
])

const isSubmitting = ref(false)
const submitSuccess = ref(false)

// 表单提交逻辑
const submitForm = async () => {
    if (isSubmitting.value) return

    // 1. 蜜罐检测 (如果隐藏 input 有值，说明是被机器脚本批量填充)
    if (honeypot.value) {
        console.warn('Honeypot triggered')
        ElMessage.error(t('product.alert.invalidRequest'))
        resetSecurityState()
        return
    }

    // 2. 人机安全验证检查
    if (!turnstileToken.value) {
        ElMessage.warning(t('product.alert.securityCheck'))
        return
    }

    isSubmitting.value = true
    try {
        const postData = {
            ...formData,
            turnstile_token: turnstileToken.value,
            honeypot: honeypot.value
        }
        await Order(postData)

        submitSuccess.value = true

        // 清空表单与重置安全状态
        formData.name = ''
        formData.email = ''
        formData.order_type = 1
        formData.remark = ''
        resetSecurityState()

        // 3秒后隐藏成功提示
        setTimeout(() => {
            submitSuccess.value = false
        }, 3000)
    } catch (error) {
        console.error('Contact form submission failed:', error)
        resetSecurityState()
        ElMessage.error(error.message || t('product.alert.submitFailed'))
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
/* 原有的所有样式保持完全不变 */
/* 1. 父容器 Banner 设置为相对定位 */
.banner {
    position: relative;
    width: 100%;
    height: 500px;
    /* 假设是全屏 Banner */
    overflow: hidden;
}

/* 背景图处理同上 */
.background-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
}

/* 2. **核心调整：使用绝对定位精确控制** */
.text-content {
    position: absolute;
    /* **关键调整：设置距离底部的距离（底部对齐）** */
    /* 调整这个值（例如 10%）可以控制文字的高低 */
    bottom: 40%;

    /* 水平居中方法 */
    left: 50%;
    transform: translateX(-50%);
    /* 水平偏移 50% 实现精确居中 */

    text-align: center;
    /* 文字内部居中 */
    color: white;
    z-index: 1;
    width: 90%;
    /* 给一个最大宽度，防止文字太多时贴边 */
}

/* 进一步精细化文字样式 */
.text-content h1 {
    font-size: 2.5rem;
    font-weight: bold;
    margin-bottom: 1rem;
}

.text-content p {
    font-size: 1.2rem;
    font-weight: normal;
}

/* =========================================
   全局与基础样式
========================================= */
.contact-page {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #333;
    line-height: 1.6;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 80px 20px;
}

.bg-light {
    background-color: #f9fafb;
}

.grid-container {
    display: grid;
    gap: 30px;
}

.grid-3 {
    grid-template-columns: repeat(3, 1fr);
}

/* =========================================
   1. 顶部视觉 (Hero)
========================================= */


.hero-content {
    max-width: 700px;
    margin: 0 auto;
}

.hero-content h1 {
    font-size: 3rem;
    margin-bottom: 20px;
    font-weight: 700;
}

.hero-content p {
    font-size: 1.1rem;
    color: #aaa;
}

/* =========================================
   2. 快捷联系方式 (Methods Grid)
========================================= */
.method-card {
    background: #fff;
    border: 1px solid #eaeaea;
    border-radius: 16px;
    padding: 40px 30px;
    text-align: center;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.method-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05);
}

.method-icon {
    font-size: 2.5rem;
    margin-bottom: 20px;
}

.method-card h3 {
    font-size: 1.3rem;
    margin-bottom: 15px;
    color: #111;
}

.method-card p {
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 25px;
    min-height: 45px;
}

.contact-link {
    color: #007aff;
    text-decoration: none;
    font-weight: 600;
    font-size: 1rem;
}

.contact-link:hover {
    text-decoration: underline;
}

/* =========================================
   3. 表单与地址区 (Split Layout)
========================================= */
.split-layout {
    display: flex;
    gap: 80px;
}

.form-container {
    flex: 3;
}

.locations-container {
    flex: 2;
}

.form-container h2,
.locations-container h2 {
    font-size: 2rem;
    margin-bottom: 10px;
    color: #111;
}

.form-subtitle {
    color: #666;
    margin-bottom: 40px;
}

/* 表单样式 */
.contact-form {
    background: #fff;
    padding: 40px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
    border: 1px solid #eaeaea;
}

.form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
}

.form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.form-group label {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: #111;
}

.form-group input,
.form-group textarea,
.form-group select {
    padding: 12px 15px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
    transition: border-color 0.2s;
    outline: none;
    background-color: #fafafa;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
    border-color: #007aff;
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

/* 下拉框自定义箭头包装器 */
.select-wrapper {
    position: relative;
}

.select-wrapper::after {
    content: '▼';
    font-size: 0.8rem;
    color: #999;
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}

.form-group select {
    width: 100%;
    appearance: none;
    cursor: pointer;
}

/* Turnstile 验证小部件容器 */
.form-turnstile {
    margin-bottom: 25px;
    display: flex;
    justify-content: flex-start;
}

/* 提交按钮 */
.btn-submit {
    background: #1a1a1a;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 15px 30px;
    font-size: 1.05rem;
    cursor: pointer;
    font-weight: 600;
    width: 100%;
    transition: all 0.3s ease;
    margin-top: 10px;
}

.btn-submit:hover:not(:disabled) {
    background: #333;
    transform: translateY(-2px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
}

.btn-submit:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.success-message {
    margin-top: 20px;
    padding: 15px;
    background-color: #e6f4ea;
    color: #137333;
    border-radius: 8px;
    text-align: center;
    font-weight: 500;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* 办公地址卡片 */
.locations-container {
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding-top: 85px;
}

.office-card {
    background: transparent;
    border-left: 3px solid #eaeaea;
    padding-left: 20px;
    transition: border-color 0.3s;
}

.office-card:hover {
    border-color: #007aff;
}

.office-card h4 {
    font-size: 1.1rem;
    margin-bottom: 10px;
    color: #111;
}

.office-card p {
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 5px;
    line-height: 1.5;
}

.office-phone {
    font-weight: 600;
    color: #333 !important;
    margin-top: 10px;
}

/* =========================================
   响应式适配
========================================= */
@media (max-width: 992px) {
    .split-layout {
        flex-direction: column;
        gap: 60px;
    }

    .locations-container {
        padding-top: 0;
    }

    .locations-container h2 {
        display: none;
        /* 手机端隐藏右侧重复的标题 */
    }

    .locations-container {
        flex-direction: row;
        flex-wrap: wrap;
    }

    .office-card {
        flex: 1;
        min-width: 250px;
    }
}

@media (max-width: 768px) {
    .grid-3 {
        grid-template-columns: 1fr;
    }

    .form-row {
        flex-direction: column;
        gap: 20px;
    }

    .contact-form {
        padding: 25px 20px;
    }

    .hero-content h1 {
        font-size: 2.2rem;
    }
}
</style>